import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Text, Flex, HStack, VStack, Badge, Button, IconButton, Image, Spinner,
  Tooltip, useToast, Input, Select, Textarea, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure, Grid,
  Link, Tag, TagLabel, Checkbox, Divider, FormLabel, FormControl,
} from '@chakra-ui/react';
import {
  FiArrowLeft, FiPlus, FiTrash2, FiPhone, FiCalendar,
  FiClock, FiFileText, FiDollarSign, FiUsers, FiBriefcase, FiCheckSquare,
  FiChevronDown, FiChevronRight, FiPackage, FiRefreshCw,
  FiCheck, FiAlertCircle, FiShoppingCart, FiPause, FiPlay,
  FiMail, FiMenu, FiPaperclip, FiBookOpen, FiStar, FiUser,
} from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import InvoiceModal from './InvoiceModal';
import CommunicationsModal from './CommunicationsModal';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmtMoney = v => v!=null ? `£${parseFloat(v).toLocaleString('en-GB',{minimumFractionDigits:2,maximumFractionDigits:2})}` : '—';
const invSC = s => {
  if(!s) return 'gray';
  if(['paid_in_full','recv_paid_in_full'].includes(s)) return 'green';
  if(['overdue','very_overdue','recv_overdue'].includes(s)) return 'red';
  if(['paid_in_part','recv_pay_within_terms'].includes(s)) return 'orange';
  if(['sent_for_payment','invoiced_within_terms'].includes(s)) return 'blue';
  return 'gray';
};
const invSL = s => (s||'Draft').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());

// ── Shared sub-components ─────────────────────────────────────────────────────
const SectionHeader = ({icon:Icon,title,count,color='gray.600',onAdd,addLabel,rightContent}) => (
  <Flex align="center" justify="space-between" mb={3}>
    <HStack spacing={2}>
      {Icon&&<Box color={color}><Icon size={15}/></Box>}
      <Text fontSize="13px" fontWeight="800" color={color} textTransform="uppercase" letterSpacing="0.05em">{title}</Text>
      {count!=null&&<Badge colorScheme="gray" fontSize="9px">{count}</Badge>}
    </HStack>
    <HStack spacing={2}>
      {rightContent}
      {onAdd&&<Button size="xs" leftIcon={<FiPlus size={11}/>} variant="outline" colorScheme="blue" onClick={onAdd}>{addLabel||'Add'}</Button>}
    </HStack>
  </Flex>
);

const Card = ({children,p=4,...props}) => (
  <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
    boxShadow="0 1px 3px rgba(0,0,0,0.06)" p={p} {...props}>{children}</Box>
);

const InvoiceRow = ({inv}) => (
  <Flex align="center" justify="space-between" py={2} px={2} borderRadius="md" _hover={{bg:'gray.50'}}>
    <HStack spacing={3} flex={1} minW={0}>
      <Box minW="80px">
        <Text fontSize="11px" fontWeight="700">{inv.invoice_number||inv.invoice_ref||`#${inv.id}`}</Text>
        <Text fontSize="9px" color="gray.400">{fmtDate(inv.issue_date)}</Text>
      </Box>
      <Badge colorScheme={invSC(inv.status)} fontSize="8px" flexShrink={0}>{invSL(inv.status)}</Badge>
      <Text fontSize="11px" color="gray.600" noOfLines={1} flex={1}>{inv.supplier_name||inv.company_name||'—'}</Text>
    </HStack>
    <Text fontSize="12px" fontWeight="700" color={inv.direction==='in'?'red.600':'green.700'} ml={3}>
      {fmtMoney(inv.total_gross)}
    </Text>
  </Flex>
);

// ── Generic "Add thing" modal ─────────────────────────────────────────────────
const QuickModal = ({isOpen,onClose,title,children,onSave,saving,saveLabel='Save'}) => (
  <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
    <ModalOverlay/><ModalContent>
      <ModalHeader fontSize="md" pb={1}>{title}</ModalHeader><ModalCloseButton/>
      <ModalBody><VStack spacing={3} align="stretch">{children}</VStack></ModalBody>
      <ModalFooter gap={2}>
        <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button size="sm" colorScheme="blue" isLoading={saving} onClick={onSave}>{saveLabel}</Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

// ── Icon button shorthand ─────────────────────────────────────────────────────
const ActionBtn = ({icon:Icon,label,color='gray',onClick,count,size=12}) => (
  <Tooltip label={label}>
    <Box position="relative" display="inline-flex">
      <IconButton icon={<Icon size={size}/>} size="xs" variant="ghost" colorScheme={color}
        onClick={e=>{e.stopPropagation();onClick&&onClick();}} aria-label={label}/>
      {count>0&&(
        <Badge position="absolute" top="-3px" right="-3px" fontSize="7px" colorScheme={color}
          variant="solid" borderRadius="full" minW="13px" h="13px" display="flex" alignItems="center" justifyContent="center">
          {count}
        </Badge>
      )}
    </Box>
  </Tooltip>
);

// ── Subtask row ───────────────────────────────────────────────────────────────
// ── Company card with drag-to-primary, involvement editing ───────────────────
const CompanyCard = ({ c, projectId, onRefresh, onRemove, companies }) => {
  const [editingInvolvement, setEditingInvolvement] = useState(false);
  const [invText, setInvText] = useState(c.involvement || '');
  const [dragging, setDragging] = useState(false);
  const toast = useToast();
  const cid = c.company_id || c.id;

  const saveInvolvement = async () => {
    try {
      await api.put(`/projects/${projectId}/companies/${cid}`, { involvement: invText });
      setEditingInvolvement(false); onRefresh();
      toast({ title: 'Saved', status: 'success', duration: 1200 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const setPrimary = async () => {
    try {
      await api.put(`/projects/${projectId}/companies/${cid}`, { is_primary: true });
      onRefresh();
    } catch(e) {}
  };

  const handleDrop = async (e, toPrimary) => {
    e.preventDefault();
    const fromCid = e.dataTransfer.getData('company_id');
    if (!fromCid || fromCid === cid) return;
    try {
      await api.put(`/projects/${projectId}/companies/${fromCid}`, { is_primary: toPrimary });
      if (toPrimary) {
        // Clear primary from others
        for (const co of companies.filter(co => (co.company_id||co.id) !== fromCid)) {
          if (co.is_primary) await api.put(`/projects/${projectId}/companies/${co.company_id||co.id}`, { is_primary: false });
        }
      }
      onRefresh();
    } catch(e) {}
  };

  return (
    <Box border="1px solid" borderColor={c.is_primary ? 'blue.200' : 'gray.200'}
      borderRadius="md" bg={c.is_primary ? 'blue.50' : 'white'} p={2}
      draggable
      onDragStart={e => { e.dataTransfer.setData('company_id', String(cid)); setDragging(true); }}
      onDragEnd={() => setDragging(false)}
      onDragOver={e => e.preventDefault()}
      onDrop={e => handleDrop(e, !c.is_primary)}
      opacity={dragging ? 0.5 : 1}
      transition="opacity 0.15s">
      <Flex align="flex-start" justify="space-between">
        <HStack spacing={2} flex={1} minW={0}>
          <Box color="gray.300" cursor="grab" flexShrink={0}><FiMenu size={12}/></Box>
          {c.logo_url && <Image src={`/api/files/${c.logo_url}`} h="18px" maxW="40px" objectFit="contain" flexShrink={0}/>}
          <Box flex={1} minW={0}>
            <HStack spacing={1}>
              <Text fontSize="12px" fontWeight="700" noOfLines={1}>{c.name}</Text>
              {!!c.is_primary && <Badge colorScheme="blue" fontSize="7px">Primary</Badge>}
            </HStack>
            {c.involvement && !editingInvolvement && (
              <Text fontSize="10px" color="gray.500" noOfLines={2} cursor="pointer"
                onClick={() => { setInvText(c.involvement||''); setEditingInvolvement(true); }}>
                {c.involvement}
              </Text>
            )}
            {!c.involvement && !editingInvolvement && (
              <Text fontSize="9px" color="gray.300" cursor="pointer" fontStyle="italic"
                onClick={() => setEditingInvolvement(true)}>
                + Add involvement description
              </Text>
            )}
            {editingInvolvement && (
              <Box mt={1}>
                <Textarea size="xs" rows={2} value={invText}
                  onChange={e => setInvText(e.target.value)}
                  placeholder="Describe this company's role…" autoFocus/>
                <HStack mt={1} spacing={1} justify="flex-end">
                  <Button size="xs" variant="ghost" onClick={() => setEditingInvolvement(false)}>Cancel</Button>
                  <Button size="xs" colorScheme="blue" onClick={saveInvolvement}>Save</Button>
                </HStack>
              </Box>
            )}
          </Box>
        </HStack>
        <VStack spacing={0.5} flexShrink={0}>
          {!c.is_primary && (
            <Tooltip label="Set as primary company">
              <IconButton icon={<FiStar size={10}/>} size="xs" variant="ghost" colorScheme="blue"
                onClick={setPrimary} aria-label="Set primary"/>
            </Tooltip>
          )}
          <IconButton icon={<FiTrash2 size={10}/>} size="xs" variant="ghost" colorScheme="red"
            onClick={() => onRemove(cid)} aria-label="Remove"/>
        </VStack>
      </Flex>
    </Box>
  );
};

const SubtaskRow = ({st, projectId, onRefresh, onClockOnSub}) => {
  const [showDesc, setShowDesc] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showComms, setShowComms] = useState(false);
  const [uploadRef] = useState(React.createRef());
  const toast = useToast();

  const toggle = e => { e.stopPropagation(); setShowDesc(p=>!p); };

  const saveNote = async () => {
    if(!noteText.trim()) return;
    try {
      await api.post('/notes',{task_id: st.task_id, content: noteText.trim(), type:'task'});
      toast({title:'Note saved',status:'success',duration:1500});
      setNoteText(''); setAddingNote(false); onRefresh();
    } catch(e){toast({title:'Failed',status:'error',duration:2000});}
  };

  const handleFileUpload = async e => {
    const file = e.target.files[0]; if(!file) return;
    const fd = new FormData();
    fd.append('file',file); fd.append('folder','task_docs');
    fd.append('entity_type','sub_task'); fd.append('entity_id', String(st.id));
    try {
      await api.post('/files/upload',fd,{headers:{'Content-Type':'multipart/form-data'}});
      toast({title:'File attached',status:'success',duration:1500}); onRefresh();
    } catch(e){toast({title:'Upload failed',status:'error',duration:2000});}
    e.target.value='';
  };

  return (
    <Box bg="white" borderRadius="md" border="1px solid" borderColor="gray.100"
      opacity={st.is_completed?0.65:1} mb={1}>
      <Flex align="center" gap={1.5} py={1.5} px={2}>
        <Checkbox size="sm" colorScheme="green" isChecked={!!st.is_completed}
          onChange={async()=>{
            try{await api.put(`/sub-tasks/${st.id}/toggle`); onRefresh();}catch(e){}
          }} flexShrink={0} onClick={e=>e.stopPropagation()}/>
        <Box flex={1} cursor="pointer" onClick={toggle} minW={0}>
          <Text fontSize="11px" fontWeight={500} noOfLines={showDesc?undefined:1}
            color={st.is_completed?'gray.400':'gray.700'}
            textDecoration={st.is_completed?'line-through':'none'}>
            {st.title||'Subtask'}
          </Text>
        </Box>
        {st.assigned_to_name&&<Text fontSize="9px" color="gray.400" flexShrink={0}>{st.assigned_to_name}</Text>}
        {/* Subtask action icons */}
        <HStack spacing={0} flexShrink={0}>
          <ActionBtn icon={st.is_completed||false?FiPause:FiPlay} label="Clock on/off subtask"
            color="teal" onClick={()=>onClockOnSub(st)}/>
          <ActionBtn icon={FiFileText} label="Add note" color="blue"
            onClick={()=>setAddingNote(p=>!p)}/>
          <ActionBtn icon={FiMail} label="View/add communications" color="purple"
            onClick={()=>setShowComms(true)} count={st.comm_count||0}/>
          <ActionBtn icon={FiPaperclip} label="Attach file" color="purple"
            onClick={()=>uploadRef.current?.click()}/>
          <input type="file" ref={uploadRef} style={{display:'none'}} onChange={handleFileUpload}/>
          {st.description&&(
            <ActionBtn icon={showDesc?FiChevronDown:FiChevronRight} label="Description"
              color="gray" onClick={()=>setShowDesc(p=>!p)}/>
          )}
        </HStack>
      </Flex>
      {showDesc&&st.description&&(
        <Box px={7} pb={1.5}>
          <Text fontSize="10px" color="gray.500" whiteSpace="pre-wrap">{st.description}</Text>
        </Box>
      )}
      {addingNote&&(
        <Box px={2} pb={2} borderTop="1px solid" borderColor="blue.100" bg="blue.50">
          <Textarea size="xs" rows={2} mt={1} value={noteText}
            onChange={e=>setNoteText(e.target.value)} placeholder="Add a note…"/>
          <HStack justify="flex-end" mt={1} spacing={1}>
            <Button size="xs" variant="ghost" onClick={()=>{setAddingNote(false);setNoteText('');}}>Cancel</Button>
            <Button size="xs" colorScheme="blue" onClick={saveNote}>Save</Button>
          </HStack>
        </Box>
      )}

      {/* Sub-task communications modal */}
      {showComms && (
        <CommunicationsModal
          projectId={projectId}
          taskId={st.task_id}
          subTaskId={st.id}
          subTaskTitle={st.title}
          isOpen={true}
          onClose={() => { setShowComms(false); onRefresh(); }}
        />
      )}
    </Box>
  );
};

// ── Task card ─────────────────────────────────────────────────────────────────
const TaskCard = ({task,index,projectId,onRefresh,onClockOn,onInvoiceIn,onInvoiceOut,onDragStart,onDragOver,onDrop,isDragOver}) => {
  const [expanded, setExpanded] = useState(false);
  const [subtasks, setSubtasks] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [subsLoaded, setSubsLoaded] = useState(false);
  const [notes, setNotes] = useState([]);
  const [files, setFiles] = useState([]);
  const [addingNote, setAddingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [progressVal, setProgressVal] = useState(task.completion_percentage||0);
  const [showProgressEdit, setShowProgressEdit] = useState(false);
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [completeModal, setCompleteModal] = useState(false);
  const [completeNote, setCompleteNote] = useState('');
  const [savingComplete, setSavingComplete] = useState(false);
  // Sub-modals
  const calDisc = useDisclosure();
  const bookingDisc = useDisclosure();
  const [resources, setResources] = useState([]);
  const [calForm, setCalForm] = useState({title:'',start_date:'',start_time:'',end_date:'',end_time:'',location:''});
  const [bookForm, setBookForm] = useState({title:'',start_date:'',start_time:'',end_date:'',resource_id:''});
  const [savingCal, setSavingCal] = useState(false);
  const [savingBook, setSavingBook] = useState(false);
  const [taskCommsOpen, setTaskCommsOpen] = useState(false);
  const uploadRef = useRef();
  const toast = useToast();

  const tid = task.id;

  const loadSubtasks = useCallback(async (force=false) => {
    if((subsLoaded&&!force)||loadingSubs) return;
    setLoadingSubs(true);
    try {
      const {data} = await api.get(`/sub-tasks?task_id=${tid}`);
      setSubtasks(Array.isArray(data)?data:[]);
      setSubsLoaded(true);
    } catch(e){}
    setLoadingSubs(false);
  },[tid,subsLoaded,loadingSubs]);

  const loadDetails = useCallback(async()=>{
    try {
      const [notesRes,filesRes] = await Promise.all([
        api.get(`/notes?task_id=${tid}`).catch(()=>({data:[]})),
        api.get(`/files?entity_type=task&entity_id=${tid}`).catch(()=>({data:[]})),
      ]);
      setNotes(Array.isArray(notesRes.data)?notesRes.data:[]);
      setFiles(Array.isArray(filesRes.data)?filesRes.data:[]);
    } catch(e){}
  },[tid]);

  const expand = () => {
    const next=!expanded; setExpanded(next);
    if(next){ loadSubtasks(); loadDetails(); }
  };

  const loadResources = async()=>{
    if(resources.length>0) return;
    try{const {data}=await api.get('/resources'); setResources(Array.isArray(data)?data:[]);}catch(e){}
  };

  const saveNote = async()=>{
    if(!noteText.trim()) return;
    try{
      await api.post('/notes',{task_id:tid,project_id:projectId,content:noteText.trim(),type:'task'});
      toast({title:'Note saved',status:'success',duration:1500});
      setNoteText(''); setAddingNote(false); loadDetails(); onRefresh();
    }catch(e){toast({title:'Failed',status:'error',duration:2000});}
  };

  const saveProgress = async(val)=>{
    try{await api.put(`/tasks/${tid}`,{completion_percentage:val}); setProgressVal(val); setShowProgressEdit(false); onRefresh();}catch(e){}
  };

  const addSubtask = async()=>{
    if(!newSubtaskTitle.trim()) return;
    try{
      await api.post('/sub-tasks',{task_id:tid,title:newSubtaskTitle.trim()});
      setNewSubtaskTitle(''); setAddingSubtask(false); loadSubtasks(true);
      toast({title:'Subtask added',status:'success',duration:1500});
    }catch(e){toast({title:'Failed',status:'error',duration:2000});}
  };

  const handleFileUpload = async e=>{
    const file=e.target.files[0]; if(!file) return;
    const fd=new FormData();
    fd.append('file',file); fd.append('folder','task_docs');
    fd.append('entity_type','task'); fd.append('entity_id',String(tid));
    try{
      await api.post('/files/upload',fd,{headers:{'Content-Type':'multipart/form-data'}});
      toast({title:'File attached',status:'success',duration:1500}); loadDetails(); onRefresh();
    }catch(e){toast({title:'Upload failed',status:'error',duration:2000});}
    e.target.value='';
  };

  const saveCal = async()=>{
    if(!calForm.title||!calForm.start_date){toast({title:'Title and date required',status:'warning',duration:2000});return;}
    setSavingCal(true);
    try{
      await api.post('/calendar',{...calForm,task_id:tid,project_id:projectId});
      toast({title:'Calendar entry added',status:'success',duration:1500});
      calDisc.onClose(); setCalForm({title:'',start_date:'',start_time:'',end_date:'',end_time:'',location:''});
      onRefresh();
    }catch(e){toast({title:'Failed',status:'error',duration:2000});}
    setSavingCal(false);
  };

  const saveBooking = async()=>{
    if(!bookForm.title||!bookForm.start_date){toast({title:'Title and date required',status:'warning',duration:2000});return;}
    setSavingBook(true);
    try{
      const payload={...bookForm,task_id:tid,project_id:projectId,status:'tentative'};
      if(bookForm.resource_id) payload.resource_ids=[bookForm.resource_id];
      delete payload.resource_id;
      await api.post('/resource-bookings',payload);
      toast({title:'Booking created',status:'success',duration:1500});
      bookingDisc.onClose(); setBookForm({title:'',start_date:'',start_time:'',end_date:'',resource_id:''});
      onRefresh();
    }catch(e){toast({title:'Failed',status:'error',duration:2000});}
    setSavingBook(false);
  };

  const handleCompleteToggle = e=>{
    e.stopPropagation();
    if(!task.is_complete){setCompleteNote(''); setCompleteModal(true);}
    else api.put(`/tasks/${tid}`,{is_complete:false}).then(onRefresh);
  };

  const confirmComplete = async()=>{
    setSavingComplete(true);
    try{
      await api.put(`/tasks/${tid}`,{is_complete:true,completion_percentage:100});
      if(completeNote.trim()) await api.post('/notes',{task_id:tid,project_id:projectId,content:`✅ Completed: ${completeNote.trim()}`,type:'task'});
      setCompleteModal(false); onRefresh();
      toast({title:'Task marked complete 🎉',status:'success',duration:2000});
    }catch(e){toast({title:'Failed',status:'error',duration:2000});}
    setSavingComplete(false);
  };

  const clockOnSub = async(st)=>{
    try{
      const {data:active}=await api.get('/work-sessions/active').catch(()=>({data:null}));
      const isOnThis=active&&active.sub_task_id===st.id;
      if(isOnThis){
        await api.put(`/work-sessions/${active.id}/stop`);
        toast({title:`Clocked off: ${st.title}`,status:'info',duration:2000});
      }else{
        if(active) await api.put(`/work-sessions/${active.id}/stop`);
        await api.post('/work-sessions',{task_id:tid,sub_task_id:st.id,project_id:projectId});
        toast({title:`Clocked on: ${st.title}`,status:'success',duration:2000});
      }
    }catch(e){toast({title:'Failed',status:'error',duration:2000});}
  };

  const pct = task.completion_percentage||progressVal||0;
  const isComplete=!!task.is_complete, isHeld=!!task.is_held;
  const isOverdue=task.deadline_date&&!isComplete&&new Date(task.deadline_date)<new Date();
  const notesCount=task.notes_count||notes.length||0;
  const commCount=task.comm_count||0;
  const filesCount=files.length||(task.file_count||0);
  const calCount=task.calendar_count||0;
  const bookingCount=task.booking_count||0;

  return(
    <>
    <Box border="1px solid" borderColor={isDragOver?'blue.400':'gray.200'}
      borderRadius="lg" overflow="hidden" bg="white" opacity={isComplete?0.75:1}
      boxShadow={isDragOver?'0 0 0 2px #3182CE33':undefined}
      onDragOver={e=>{e.preventDefault();onDragOver(index);}}
      onDrop={e=>{e.preventDefault();onDrop(index);}}>

      {/* Progress bar */}
      {pct>0&&!isComplete&&(
        <Box h="3px" bg="gray.100">
          <Box h="3px" w={`${pct}%`} bg={pct>=80?'green.400':pct>=40?'blue.400':'orange.400'} transition="width 0.3s"/>
        </Box>
      )}

      {/* Main header row */}
      <Flex align="center" px={2} py={2.5} gap={1.5}
        bg={isComplete?'green.50':isHeld?'orange.50':isOverdue?'red.50':'white'}>

        {/* Drag handle */}
        <Box color="gray.300" cursor="grab" flexShrink={0}
          draggable onDragStart={()=>onDragStart(index)} onClick={e=>e.stopPropagation()}
          _hover={{color:'gray.500'}} px={0.5}>
          <FiMenu size={13}/>
        </Box>

        {/* Completion tick */}
        <Checkbox isChecked={isComplete} colorScheme="green" size="sm"
          onChange={handleCompleteToggle} onClick={e=>e.stopPropagation()} flexShrink={0}/>

        {/* Title — click to expand */}
        <Flex flex={1} align="center" gap={1.5} cursor="pointer" onClick={expand} minW={0}>
          <Box color="gray.400" flexShrink={0}>{expanded?<FiChevronDown size={12}/>:<FiChevronRight size={12}/>}</Box>
          <Text flex={1} fontSize="13px" fontWeight="600" noOfLines={1}
            color={isComplete?'green.700':isOverdue?'red.700':'gray.800'}
            textDecoration={isComplete?'line-through':'none'} opacity={isComplete?0.65:1}>
            {task.task_title||'Untitled task'}
          </Text>
          {isHeld&&<Badge colorScheme="orange" fontSize="8px" flexShrink={0}>HELD</Badge>}
          {isOverdue&&<Badge colorScheme="red" fontSize="8px" flexShrink={0}>OVERDUE</Badge>}
        </Flex>

        {/* Action icons */}
        <HStack spacing={0} flexShrink={0} onClick={e=>e.stopPropagation()}>
          <ActionBtn icon={FiClock} label="Clock on/off" color="teal" onClick={()=>onClockOn(task)}/>
          <ActionBtn icon={FiFileText} label="Add note" color="blue" onClick={()=>{if(!expanded)expand(); setAddingNote(p=>!p);}} count={notesCount}/>
          <ActionBtn icon={FiPaperclip} label="Attach document" color="purple"
            onClick={()=>uploadRef.current?.click()} count={filesCount}/>
          <ActionBtn icon={FiCalendar} label="Add calendar entry" color="blue"
            onClick={()=>{calDisc.onOpen();}} count={calCount}/>
          <ActionBtn icon={FiPackage} label="Add resource booking" color="pink"
            onClick={()=>{bookingDisc.onOpen();loadResources();}} count={bookingCount}/>
          <Tooltip label={`Progress: ${pct}%`}>
            <Box cursor="pointer" onClick={()=>setShowProgressEdit(p=>!p)} px={1}>
              <Text fontSize="9px" fontWeight="700"
                color={pct>=80?'green.600':pct>=40?'blue.600':'orange.500'}>{pct}%</Text>
            </Box>
          </Tooltip>
          <ActionBtn icon={FiMail} label="View/add communications" color="purple"
            onClick={()=>setTaskCommsOpen(true)} count={commCount}/>
          <ActionBtn icon={FiDollarSign} label="£ In (client invoices)" color="green"
            onClick={()=>onInvoiceIn(task)} count={task.inv_in_count||0}/>
          <ActionBtn icon={FiDollarSign} label="£ Out (supplier invoices)" color="red"
            onClick={()=>onInvoiceOut(task)} count={task.inv_out_count||0}/>
        </HStack>
        <input type="file" ref={uploadRef} style={{display:'none'}} onChange={handleFileUpload}/>
      </Flex>

      {/* Progress edit */}
      {showProgressEdit&&(
        <Box px={3} pb={2} bg="blue.50" borderTop="1px solid" borderColor="blue.100"
          onClick={e=>e.stopPropagation()}>
          <Text fontSize="10px" color="blue.600" mb={1} fontWeight="600">Set completion: {progressVal}%</Text>
          <HStack spacing={2}>
            <input type="range" min={0} max={100} step={5} value={progressVal}
              onChange={e=>setProgressVal(parseInt(e.target.value))}
              style={{flex:1,cursor:'pointer'}}/>
            <Button size="xs" colorScheme="blue" onClick={()=>saveProgress(progressVal)}>Save</Button>
            <Button size="xs" variant="ghost" onClick={()=>setShowProgressEdit(false)}>✕</Button>
          </HStack>
        </Box>
      )}

      {/* Inline note */}
      {addingNote&&(
        <Box px={3} py={2} bg="blue.50" borderTop="1px solid" borderColor="blue.100">
          <Textarea size="xs" rows={2} value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Add a note…" mb={1}/>
          <HStack justify="flex-end" spacing={1}>
            <Button size="xs" variant="ghost" onClick={()=>{setAddingNote(false);setNoteText('');}}>Cancel</Button>
            <Button size="xs" colorScheme="blue" onClick={saveNote}>Save Note</Button>
          </HStack>
        </Box>
      )}

      {/* Expanded panel */}
      {expanded&&(
        <Box px={3} py={2.5} borderTop="1px solid" borderColor="gray.100" bg="gray.50">

          {/* Description */}
          {task.description&&(
            <Box mb={3} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="gray.100">
              <Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase" mb={1}>Description</Text>
              <Text fontSize="12px" color="gray.700" whiteSpace="pre-wrap">{task.description}</Text>
            </Box>
          )}

          {/* Dates */}
          {(task.deadline_date||task.commissioning_date)&&(
            <HStack spacing={4} mb={3} flexWrap="wrap">
              {task.commissioning_date&&<HStack spacing={1}><FiCalendar size={10}/><Text fontSize="10px" color="gray.500">Start: {fmtDate(task.commissioning_date)}</Text></HStack>}
              {task.deadline_date&&<HStack spacing={1}><FiAlertCircle size={10} color={isOverdue?'red':'gray'}/><Text fontSize="10px" color={isOverdue?'red.600':'gray.500'}>Deadline: {fmtDate(task.deadline_date)}</Text></HStack>}
            </HStack>
          )}

          {/* Notes list */}
          {notes.length>0&&(
            <Box mb={3}>
              <Text fontSize="10px" color="blue.500" fontWeight="700" textTransform="uppercase" mb={1.5}>Notes ({notes.length})</Text>
              <VStack spacing={1} align="stretch">
                {notes.map(n=>(
                  <Box key={n.id} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="blue.100">
                    <Text fontSize="11px" color="gray.700" whiteSpace="pre-wrap">{n.body||n.content||n.subject||'—'}</Text>
                    <Text fontSize="9px" color="gray.400" mt={0.5}>{fmtDate(n.note_date||n.created_at)} · {n.created_by_name||'—'}</Text>
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {/* Files list */}
          {files.length>0&&(
            <Box mb={3}>
              <Text fontSize="10px" color="purple.500" fontWeight="700" textTransform="uppercase" mb={1.5}>Documents ({files.length})</Text>
              <VStack spacing={1} align="stretch">
                {files.map(f=>(
                  <Flex key={f.id} align="center" gap={2} p={1.5} bg="white" borderRadius="md" border="1px solid" borderColor="purple.100">
                    <FiPaperclip size={10} color="#805AD5"/>
                    <Link href={`/api/files/${f.folder}/${f.stored_name}`} isExternal fontSize="11px" color="blue.600" flex={1} noOfLines={1}>
                      {f.original_name||f.stored_name}
                    </Link>
                    <Text fontSize="9px" color="gray.400">{fmtDate(f.created_at)}</Text>
                  </Flex>
                ))}
              </VStack>
            </Box>
          )}

          {/* Subtasks */}
          <Box>
            <Text fontSize="10px" color="gray.500" fontWeight="700" textTransform="uppercase" mb={1.5}>
              Subtasks {subtasks.length>0&&`(${subtasks.length})`}
            </Text>
            {loadingSubs?<Spinner size="xs" my={1}/>:(
              <VStack spacing={0} align="stretch">
                {subtasks.map(st=>(
                  <SubtaskRow key={st.id} st={st} projectId={projectId}
                    onRefresh={()=>loadSubtasks(true)} onClockOnSub={clockOnSub}/>
                ))}
                {addingSubtask?(
                  <Flex gap={1} mt={1}>
                    <Input size="xs" placeholder="Subtask title…" value={newSubtaskTitle} flex={1}
                      onChange={e=>setNewSubtaskTitle(e.target.value)}
                      onKeyDown={e=>{if(e.key==='Enter')addSubtask();if(e.key==='Escape'){setAddingSubtask(false);setNewSubtaskTitle('');}}}
                      autoFocus/>
                    <Button size="xs" colorScheme="blue" onClick={addSubtask}>Add</Button>
                    <Button size="xs" variant="ghost" onClick={()=>{setAddingSubtask(false);setNewSubtaskTitle('');}}>✕</Button>
                  </Flex>
                ):(
                  <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FiPlus size={10}/>}
                    onClick={()=>setAddingSubtask(true)} alignSelf="flex-start" mt={1}>
                    + Add Sub-task
                  </Button>
                )}
              </VStack>
            )}
          </Box>
        </Box>
      )}
    </Box>

    {/* Complete modal */}
    <Modal isOpen={completeModal} onClose={()=>setCompleteModal(false)} size="sm" isCentered>
      <ModalOverlay/><ModalContent>
        <ModalHeader fontSize="md" pb={1}>🎉 Mark Task Complete</ModalHeader><ModalCloseButton/>
        <ModalBody>
          <Text fontSize="13px" fontWeight="600" mb={3}>{task.task_title}</Text>
          <Text fontSize="12px" color="gray.600" mb={2}>Log what was achieved (optional):</Text>
          <Textarea size="sm" rows={3} placeholder="e.g. Delivered final edit, received client sign-off…"
            value={completeNote} onChange={e=>setCompleteNote(e.target.value)}/>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={()=>setCompleteModal(false)}>Cancel</Button>
          <Button size="sm" colorScheme="green" isLoading={savingComplete} onClick={confirmComplete}>Mark Complete</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Calendar modal */}
    <QuickModal isOpen={calDisc.isOpen} onClose={calDisc.onClose}
      title={`📅 Add Calendar Entry — ${task.task_title}`}
      onSave={saveCal} saving={savingCal} saveLabel="Add Entry">
      <FormControl isRequired><FormLabel fontSize="xs">Title</FormLabel>
        <Input size="sm" value={calForm.title} onChange={e=>setCalForm(p=>({...p,title:e.target.value}))}
          placeholder="e.g. Production meeting"/></FormControl>
      <HStack spacing={2}>
        <FormControl isRequired flex={1}><FormLabel fontSize="xs">Start Date</FormLabel>
          <Input size="sm" type="date" value={calForm.start_date} onChange={e=>setCalForm(p=>({...p,start_date:e.target.value}))}/></FormControl>
        <FormControl flex={1}><FormLabel fontSize="xs">Start Time</FormLabel>
          <Input size="sm" type="time" value={calForm.start_time} onChange={e=>setCalForm(p=>({...p,start_time:e.target.value}))}/></FormControl>
      </HStack>
      <HStack spacing={2}>
        <FormControl flex={1}><FormLabel fontSize="xs">End Date</FormLabel>
          <Input size="sm" type="date" value={calForm.end_date} onChange={e=>setCalForm(p=>({...p,end_date:e.target.value}))}/></FormControl>
        <FormControl flex={1}><FormLabel fontSize="xs">End Time</FormLabel>
          <Input size="sm" type="time" value={calForm.end_time} onChange={e=>setCalForm(p=>({...p,end_time:e.target.value}))}/></FormControl>
      </HStack>
      <FormControl><FormLabel fontSize="xs">Location</FormLabel>
        <Input size="sm" value={calForm.location} onChange={e=>setCalForm(p=>({...p,location:e.target.value}))}
          placeholder="Office, Zoom, etc."/></FormControl>
    </QuickModal>

    {/* Resource booking modal */}
    <QuickModal isOpen={bookingDisc.isOpen} onClose={bookingDisc.onClose}
      title={`📦 Resource Booking — ${task.task_title}`}
      onSave={saveBooking} saving={savingBook} saveLabel="Create Booking">
      <FormControl isRequired><FormLabel fontSize="xs">Booking Title</FormLabel>
        <Input size="sm" value={bookForm.title} onChange={e=>setBookForm(p=>({...p,title:e.target.value}))}
          placeholder="e.g. Studio A — recording session"/></FormControl>
      <FormControl><FormLabel fontSize="xs">Resource</FormLabel>
        <Select size="sm" value={bookForm.resource_id} onChange={e=>setBookForm(p=>({...p,resource_id:e.target.value}))}>
          <option value="">— Any / no specific resource —</option>
          {resources.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
        </Select></FormControl>
      <HStack spacing={2}>
        <FormControl isRequired flex={1}><FormLabel fontSize="xs">Start Date</FormLabel>
          <Input size="sm" type="date" value={bookForm.start_date} onChange={e=>setBookForm(p=>({...p,start_date:e.target.value}))}/></FormControl>
        <FormControl flex={1}><FormLabel fontSize="xs">Start Time</FormLabel>
          <Input size="sm" type="time" value={bookForm.start_time} onChange={e=>setBookForm(p=>({...p,start_time:e.target.value}))}/></FormControl>
      </HStack>
      <FormControl><FormLabel fontSize="xs">End Date</FormLabel>
        <Input size="sm" type="date" value={bookForm.end_date} onChange={e=>setBookForm(p=>({...p,end_date:e.target.value}))}/></FormControl>
    </QuickModal>

    {/* Task-level communications modal — shows task + all sub-task comms */}
    {taskCommsOpen && (
      <CommunicationsModal
        projectId={projectId}
        taskId={tid}
        isOpen={true}
        onClose={() => { setTaskCommsOpen(false); onRefresh(); }}
      />
    )}
    </>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export default function ProjectFocusDashboard({projectId, onBack}) {
  const toast = useToast();
  const [project,setProject]=useState(null);
  const [companies,setCompanies]=useState([]);
  const [contacts,setContacts]=useState([]);
  const [users,setUsers]=useState([]);
  const [suppliers,setSuppliers]=useState([]);
  const [tasks,setTasks]=useState([]);
  const [invoicesIn,setInvoicesIn]=useState([]);
  const [invoicesOut,setInvoicesOut]=useState([]);
  const [calendar,setCalendar]=useState([]);
  const [bookings,setBookings]=useState([]);
  const [loading,setLoading]=useState(true);
  const invInDisc=useDisclosure(), invOutDisc=useDisclosure();
  const [activeTaskId,setActiveTaskId]=useState(null);
  const addContactDisc=useDisclosure(), addCompanyDisc=useDisclosure(), addTaskDisc=useDisclosure();
  const [addTaskForm,setAddTaskForm]=useState({task_title:'',description:'',deadline_date:''});
  const [addContactSearch,setAddContactSearch]=useState('');
  const [addContactResults,setAddContactResults]=useState([]);
  const [addCompanySearch,setAddCompanySearch]=useState('');
  const [addCompanyResults,setAddCompanyResults]=useState([]);
  const [saving,setSaving]=useState(false);
  const dragIdx=useRef(null);
  const [dragOver,setDragOver]=useState(null);

  const fetchAll=useCallback(async()=>{
    if(!projectId) return;
    setLoading(true);
    try{
      const [pR,coR,ctR,uR,tR,iiR,ioR,calR,bkR]=await Promise.all([
        api.get(`/projects/${projectId}`).catch(()=>({data:null})),
        api.get(`/projects/${projectId}/companies`).catch(()=>({data:[]})),
        api.get(`/projects/${projectId}/contacts`).catch(()=>({data:[]})),
        api.get(`/projects/${projectId}/users`).catch(()=>({data:[]})),
        api.get(`/tasks?project_id=${projectId}`).catch(()=>({data:[]})),
        api.get(`/invoices?project_id=${projectId}&direction=out`).catch(()=>({data:[]})),
        api.get(`/invoices?project_id=${projectId}&direction=in`).catch(()=>({data:[]})),
        api.get(`/calendar?project_id=${projectId}`).catch(()=>({data:[]})),
        api.get(`/resource-bookings?project_id=${projectId}&limit=20`).catch(()=>({data:[]})),
      ]);
      setProject(pR.data);
      setCompanies(Array.isArray(coR.data)?coR.data:[]);
      setContacts(Array.isArray(ctR.data)?ctR.data:[]);
      setUsers(Array.isArray(uR.data)?uR.data:[]);
      setTasks((Array.isArray(tR.data)?tR.data:[]).filter(t=>!t.deleted_at));
      setInvoicesIn(Array.isArray(iiR.data)?iiR.data:[]);
      setInvoicesOut(Array.isArray(ioR.data)?ioR.data:[]);
      setCalendar((Array.isArray(calR.data)?calR.data:[]).filter(e=>!e.deleted_at));
      setBookings((Array.isArray(bkR.data)?bkR.data:[]).filter(b=>!b.deleted_at));
      try{const{data:s}=await api.get(`/projects/${projectId}/suppliers`).catch(()=>({data:[]}));setSuppliers(Array.isArray(s)?s:[]);}catch(e){}
    }catch(e){}
    setLoading(false);
  },[projectId]);

  useEffect(()=>{fetchAll();},[fetchAll]);

  const handleDragStart=i=>{dragIdx.current=i;};
  const handleDragOver=i=>setDragOver(i);
  const handleDrop=async dropIdx=>{
    const from=dragIdx.current;
    if(from==null||from===dropIdx){dragIdx.current=null;setDragOver(null);return;}
    const arr=[...tasks];
    const [mv]=arr.splice(from,1); arr.splice(dropIdx,0,mv);
    setTasks(arr); dragIdx.current=null; setDragOver(null);
    try{await Promise.all(arr.map((t,i)=>api.put(`/tasks/${t.id}`,{sort_order:i*10})));}catch(e){}
  };

  const searchContacts=async q=>{
    if(q.length<1){setAddContactResults([]);return;}
    try{const{data}=await api.get(`/contacts/search?q=${encodeURIComponent(q)}`);setAddContactResults(data);}catch(e){}
  };
  const searchCompanies=async q=>{
    if(q.length<1){setAddCompanyResults([]);return;}
    try{const{data}=await api.get(`/companies/search?q=${encodeURIComponent(q)}&limit=8`);setAddCompanyResults(data);}catch(e){}
  };
  const addContact=async c=>{
    const existing=contacts.map(ct=>ct.id||ct.contact_id);
    await api.put(`/projects/${projectId}/contacts`,{contact_ids:[...existing,c.id]});
    setAddContactSearch('');setAddContactResults([]);fetchAll();
    toast({title:'Contact added',status:'success',duration:1500});
  };
  const removeContact=async cid=>{
    const ids=contacts.filter(c=>(c.id||c.contact_id)!==cid).map(c=>c.id||c.contact_id);
    await api.put(`/projects/${projectId}/contacts`,{contact_ids:ids});fetchAll();
  };
  const addCompany=async c=>{
    await api.post(`/projects/${projectId}/companies`,{company_id:c.id});
    setAddCompanySearch('');setAddCompanyResults([]);fetchAll();
    toast({title:'Company added',status:'success',duration:1500});
  };
  const removeCompany=async cid=>{
    try{await api.delete(`/projects/${projectId}/companies/${cid}`);}catch(e){}fetchAll();
  };
  const addTask=async()=>{
    if(!addTaskForm.task_title) return;
    setSaving(true);
    try{
      await api.post('/tasks',{...addTaskForm,project_id:projectId});
      toast({title:'Task added',status:'success',duration:1500});
      addTaskDisc.onClose();setAddTaskForm({task_title:'',description:'',deadline_date:''});fetchAll();
    }catch(e){toast({title:'Failed',status:'error',duration:2000});}
    setSaving(false);
  };
  const clockOn=async task=>{
    try{
      const{data:active}=await api.get('/work-sessions/active').catch(()=>({data:null}));
      const isOnThis=active&&active.task_id===task.id&&!active.sub_task_id;
      if(isOnThis){await api.put(`/work-sessions/${active.id}/stop`);toast({title:`Clocked off: ${task.task_title}`,status:'info',duration:2000});}
      else{if(active)await api.put(`/work-sessions/${active.id}/stop`);await api.post('/work-sessions',{task_id:task.id,project_id:projectId});toast({title:`Clocked on: ${task.task_title}`,status:'success',duration:2000});}
      fetchAll();
    }catch(e){toast({title:'Failed',status:'error',duration:2000});}
  };

  const totalIn=invoicesIn.reduce((s,i)=>s+parseFloat(i.total_gross||0),0);
  const totalOut=invoicesOut.reduce((s,i)=>s+parseFloat(i.total_gross||0),0);
  const balance=totalIn-totalOut;
  const completedTasks=tasks.filter(t=>t.is_complete).length;
  const overallPct=tasks.length>0?Math.round(completedTasks/tasks.length*100):0;

  if(loading) return(
    <Flex h="100vh" align="center" justify="center" direction="column" gap={3}>
      <Spinner size="xl" color="blue.400" thickness="3px"/>
      <Text fontSize="13px" color="gray.400">Loading project…</Text>
    </Flex>
  );
  if(!project) return <Flex h="100vh" align="center" justify="center"><Text color="gray.400">Project not found</Text></Flex>;

  return(
    <Box minH="100vh" bg="gray.50">
      {/* Top bar */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.200" px={6} py={3}
        position="sticky" top={0} zIndex={10} boxShadow="sm">
        <Flex align="center" justify="space-between">
          <HStack spacing={3}>
            <IconButton icon={<FiArrowLeft/>} size="sm" variant="ghost" onClick={onBack} aria-label="Back"/>
            {project.project_logo_url&&<Image src={`/api/files/${project.project_logo_url}`} h="32px" maxW="80px" objectFit="contain"/>}
            <Box>
              <Text fontSize="18px" fontWeight="800" color="gray.900" lineHeight={1.1}>{project.project_name||'Project'}</Text>
              {project.company_name&&<Text fontSize="11px" color="gray.500">{project.company_name}</Text>}
            </Box>
            {project.is_held&&<Badge colorScheme="orange">HELD</Badge>}
            {project.is_done&&<Badge colorScheme="green">DONE</Badge>}
            {tasks.length>0&&(
              <HStack spacing={1.5}>
                <Box w="80px" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                  <Box h="full" w={`${overallPct}%`} bg={overallPct===100?'green.400':'blue.400'} borderRadius="full" transition="width 0.5s"/>
                </Box>
                <Text fontSize="10px" color="gray.500">{overallPct}% ({completedTasks}/{tasks.length})</Text>
              </HStack>
            )}
          </HStack>
          <HStack spacing={2}>
            <HStack spacing={0} bg="gray.50" borderRadius="lg" px={3} py={1.5} border="1px solid" borderColor="gray.200">
              {[['£ In',totalIn,'green.600'],['£ Out',totalOut,'red.500'],['Balance',balance,balance>=0?'green.700':'red.600']].map(([label,val,col],i)=>(
                <React.Fragment key={label}>
                  {i>0&&<Box w="1px" h="28px" bg="gray.200" mx={2}/>}
                  <Box textAlign="center">
                    <Text fontSize="9px" color="gray.400" fontWeight="700" textTransform="uppercase">{label}</Text>
                    <Text fontSize="14px" fontWeight="800" color={col}>{fmtMoney(val)}</Text>
                  </Box>
                </React.Fragment>
              ))}
            </HStack>
            <IconButton icon={<FiRefreshCw size={14}/>} size="sm" variant="ghost" onClick={fetchAll} aria-label="Refresh"/>
          </HStack>
        </Flex>
      </Box>

      {/* Body */}
      <Box px={6} py={5} maxW="1400px" mx="auto">
        <Grid templateColumns={{base:'1fr',lg:'300px 1fr'}} gap={5}>
          {/* Left */}
          <VStack spacing={4} align="stretch">
            <Card>
              <SectionHeader icon={FiBriefcase} title="Companies" count={companies.length}
                color="blue.600" onAdd={addCompanyDisc.onOpen} addLabel="Add"/>
              {companies.length===0?<Text fontSize="11px" color="gray.400">None linked</Text>:(
                <VStack spacing={1} align="stretch">
                  {/* Primary */}
                  {companies.filter(c=>!!c.is_primary).length>0&&(
                    <Text fontSize="9px" fontWeight="800" color="blue.500" textTransform="uppercase" letterSpacing="0.06em" mb={0.5}>Primary</Text>
                  )}
                  {companies.filter(c=>!!c.is_primary).map(c=>(
                    <CompanyCard key={c.company_id||c.id} c={c} projectId={projectId} onRefresh={fetchAll}
                      onRemove={removeCompany} companies={companies}/>
                  ))}
                  {/* Associated */}
                  {companies.filter(c=>!c.is_primary).length>0&&(
                    <Text fontSize="9px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.06em" mt={1} mb={0.5}>Associated</Text>
                  )}
                  {companies.filter(c=>!c.is_primary).map(c=>(
                    <CompanyCard key={c.company_id||c.id} c={c} projectId={projectId} onRefresh={fetchAll}
                      onRemove={removeCompany} companies={companies}/>
                  ))}
                  {companies.every(c=>!c.is_primary)&&companies.length>0&&(
                    <Text fontSize="9px" color="orange.500" mt={1}>Drag a company to "Primary" or right-click to set</Text>
                  )}
                </VStack>
              )}
            </Card>
            <Card>
              <SectionHeader icon={FiUsers} title="Contacts" count={contacts.length}
                color="purple.600" onAdd={addContactDisc.onOpen} addLabel="Add"/>
              {contacts.length===0?<Text fontSize="11px" color="gray.400">None linked</Text>:(
                <VStack spacing={2} align="stretch">
                  {contacts.map(c=>{
                    const cid=c.id||c.contact_id;
                    return(
                      <Flex key={cid} align="center" justify="space-between" p={2}
                        borderRadius="md" border="1px solid" borderColor="gray.100">
                        <HStack spacing={2}>
                          {c.avatar_url?<Image src={`/api/files/${c.avatar_url}`} w="28px" h="28px" borderRadius="full" objectFit="cover"/>
                            :<Flex w="28px" h="28px" borderRadius="full" bg="purple.100" align="center" justify="center" flexShrink={0}>
                              <Text fontSize="10px" fontWeight="700" color="purple.600">{(c.first_name?.[0]||'')+(c.last_name?.[0]||'')}</Text>
                            </Flex>}
                          <Box>
                            <Text fontSize="12px" fontWeight="600">{c.first_name} {c.last_name||''}</Text>
                            {c.job_title&&<Text fontSize="10px" color="gray.500">{c.job_title}</Text>}
                            {c.email&&<Link href={`mailto:${c.email}`} fontSize="10px" color="blue.500">{c.email}</Link>}
                          </Box>
                        </HStack>
                        <VStack spacing={0.5}>
                          {c.mobile&&<Tooltip label={c.mobile}><Link href={`tel:${c.mobile}`}><IconButton icon={<FiPhone size={10}/>} size="xs" variant="ghost" colorScheme="green" aria-label="Call"/></Link></Tooltip>}
                          <IconButton icon={<FiTrash2 size={10}/>} size="xs" variant="ghost" colorScheme="red"
                            onClick={()=>removeContact(cid)} aria-label="Remove"/>
                        </VStack>
                      </Flex>
                    );
                  })}
                </VStack>
              )}
            </Card>
            {suppliers.length>0&&(
              <Card>
                <SectionHeader icon={FiShoppingCart} title="Suppliers" count={suppliers.length} color="orange.600"/>
                <VStack spacing={1.5} align="stretch">
                  {suppliers.map(s=>(
                    <Flex key={s.id} align="center" gap={2} p={2} borderRadius="md" border="1px solid" borderColor="orange.100" bg="orange.50">
                      <FiShoppingCart size={12} color="orange"/>
                      <Text fontSize="12px" fontWeight="600">{s.name||s.company_name||'—'}</Text>
                    </Flex>
                  ))}
                </VStack>
              </Card>
            )}
            {users.length>0&&(
              <Card>
                <SectionHeader icon={FiUsers} title="Team" count={users.length} color="teal.600"/>
                <Flex gap={2} flexWrap="wrap">
                  {users.map(u=><Tag key={u.user_id} colorScheme="teal" size="sm"><TagLabel>{u.display_name||u.username}</TagLabel></Tag>)}
                </Flex>
              </Card>
            )}
            {calendar.length>0&&(
              <Card>
                <SectionHeader icon={FiCalendar} title="Schedule" count={calendar.length} color="blue.500"/>
                <VStack spacing={1.5} align="stretch">
                  {calendar.slice(0,8).map(e=>(
                    <Flex key={e.id} align="center" gap={2} py={1.5} px={2} borderRadius="md" border="1px solid" borderColor="blue.100" bg="blue.50">
                      <FiCalendar size={11} color="#3182CE"/>
                      <Box flex={1} minW={0}>
                        <Text fontSize="11px" fontWeight="600" noOfLines={1}>{e.title||'Event'}</Text>
                        <Text fontSize="10px" color="gray.500">{fmtDate(e.start_date||e.date)}{e.start_time?` @ ${e.start_time}`:''}</Text>
                      </Box>
                    </Flex>
                  ))}
                </VStack>
              </Card>
            )}
            {bookings.length>0&&(
              <Card>
                <SectionHeader icon={FiPackage} title="Resource Bookings" count={bookings.length} color="pink.600"/>
                <VStack spacing={1.5} align="stretch">
                  {bookings.slice(0,6).map(b=>(
                    <Flex key={b.id} align="center" gap={2} py={1.5} px={2} borderRadius="md" border="1px solid" borderColor="pink.100" bg="pink.50">
                      <FiPackage size={11} color="#D53F8C"/>
                      <Box flex={1} minW={0}>
                        <Text fontSize="11px" fontWeight="600" noOfLines={1}>{b.title||b.resource_name||'Booking'}</Text>
                        <Text fontSize="10px" color="gray.500">{fmtDate(b.start_date)}{b.start_time?` @ ${b.start_time}`:''}</Text>
                      </Box>
                      <Badge colorScheme={b.status==='confirmed'?'green':b.status==='tentative'?'yellow':'gray'} fontSize="8px">{b.status||'pending'}</Badge>
                    </Flex>
                  ))}
                </VStack>
              </Card>
            )}
          </VStack>

          {/* Right */}
          <VStack spacing={4} align="stretch">
            <Card p={0}>
              <Box px={4} pt={4} pb={2}>
                <SectionHeader icon={FiDollarSign} title="Client Invoices (£ In)" count={invoicesIn.length} color="green.600"
                  onAdd={()=>{setActiveTaskId(null);invInDisc.onOpen();}} addLabel="New"/>
              </Box>
              {invoicesIn.length===0?<Box px={4} pb={4}><Text fontSize="11px" color="gray.400">No client invoices yet</Text></Box>:(
                <Box px={2} pb={2}>
                  {invoicesIn.map(inv=><InvoiceRow key={inv.id} inv={inv}/>)}
                  <Flex justify="flex-end" px={2} pt={2} mt={1} borderTop="1px solid" borderColor="gray.100">
                    <Text fontSize="12px" fontWeight="700" color="green.700">Total: {fmtMoney(totalIn)}</Text>
                  </Flex>
                </Box>
              )}
            </Card>
            <Card p={0}>
              <Box px={4} pt={4} pb={2}>
                <SectionHeader icon={FiDollarSign} title="Supplier Invoices (£ Out)" count={invoicesOut.length} color="red.600"
                  onAdd={()=>{setActiveTaskId(null);invOutDisc.onOpen();}} addLabel="New"/>
              </Box>
              {invoicesOut.length===0?<Box px={4} pb={4}><Text fontSize="11px" color="gray.400">No supplier invoices yet</Text></Box>:(
                <Box px={2} pb={2}>
                  {invoicesOut.map(inv=><InvoiceRow key={inv.id} inv={inv}/>)}
                  <Flex justify="flex-end" px={2} pt={2} mt={1} borderTop="1px solid" borderColor="gray.100">
                    <Text fontSize="12px" fontWeight="700" color="red.600">Total: {fmtMoney(totalOut)}</Text>
                  </Flex>
                </Box>
              )}
            </Card>
            <Card p={0}>
              <Box px={4} pt={4} pb={2}>
                <SectionHeader icon={FiCheckSquare} title="Tasks" count={tasks.length} color="gray.700"
                  onAdd={addTaskDisc.onOpen} addLabel="New Task"/>
              </Box>
              {tasks.length===0?<Box px={4} pb={4}><Text fontSize="11px" color="gray.400">No tasks yet</Text></Box>:(
                <VStack spacing={2} px={3} pb={3} align="stretch">
                  {tasks.map((task,idx)=>(
                    <TaskCard key={task.id} task={task} index={idx} projectId={projectId}
                      onRefresh={fetchAll} onClockOn={clockOn}
                      onInvoiceIn={t=>{setActiveTaskId(t.id);invInDisc.onOpen();}}
                      onInvoiceOut={t=>{setActiveTaskId(t.id);invOutDisc.onOpen();}}
                      onDragStart={handleDragStart} onDragOver={handleDragOver}
                      onDrop={handleDrop} isDragOver={dragOver===idx}/>
                  ))}
                </VStack>
              )}
            </Card>
          </VStack>
        </Grid>
      </Box>

      {/* Invoice modals */}
      <InvoiceModal projectId={projectId} taskId={activeTaskId} direction="in"
        isOpen={invInDisc.isOpen} onClose={()=>{invInDisc.onClose();fetchAll();}}/>
      <InvoiceModal projectId={projectId} taskId={activeTaskId} direction="out"
        isOpen={invOutDisc.isOpen} onClose={()=>{invOutDisc.onClose();fetchAll();}}/>

      {/* Add Contact */}
      <Modal isOpen={addContactDisc.isOpen} onClose={addContactDisc.onClose} size="sm" isCentered>
        <ModalOverlay/><ModalContent>
          <ModalHeader fontSize="md">Add Contact</ModalHeader><ModalCloseButton/>
          <ModalBody>
            <Input size="sm" placeholder="Search contacts…" value={addContactSearch}
              onChange={e=>{setAddContactSearch(e.target.value);searchContacts(e.target.value);}}/>
            {addContactResults.length>0&&(
              <Box border="1px" borderColor="gray.200" borderRadius="md" mt={2} maxH="200px" overflowY="auto">
                {addContactResults.map(c=>(
                  <Box key={c.id} px={3} py={2} fontSize="12px" cursor="pointer" _hover={{bg:'purple.50'}}
                    onClick={()=>{addContact(c);addContactDisc.onClose();}}>
                    {c.first_name} {c.last_name||''} {c.company_name?`(${c.company_name})`:''}
                  </Box>
                ))}
              </Box>
            )}
          </ModalBody>
          <ModalFooter><Button size="sm" variant="ghost" onClick={addContactDisc.onClose}>Cancel</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Company */}
      <Modal isOpen={addCompanyDisc.isOpen} onClose={addCompanyDisc.onClose} size="sm" isCentered>
        <ModalOverlay/><ModalContent>
          <ModalHeader fontSize="md">Add Company</ModalHeader><ModalCloseButton/>
          <ModalBody>
            <Input size="sm" placeholder="Search companies…" value={addCompanySearch}
              onChange={e=>{setAddCompanySearch(e.target.value);searchCompanies(e.target.value);}}/>
            {addCompanyResults.length>0&&(
              <Box border="1px" borderColor="gray.200" borderRadius="md" mt={2} maxH="200px" overflowY="auto">
                {addCompanyResults.map(c=>(
                  <Box key={c.id} px={3} py={2} fontSize="12px" cursor="pointer" _hover={{bg:'blue.50'}}
                    onClick={()=>{addCompany(c);addCompanyDisc.onClose();}}>
                    {c.name}
                  </Box>
                ))}
              </Box>
            )}
          </ModalBody>
          <ModalFooter><Button size="sm" variant="ghost" onClick={addCompanyDisc.onClose}>Cancel</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Task */}
      <Modal isOpen={addTaskDisc.isOpen} onClose={addTaskDisc.onClose} size="md" isCentered>
        <ModalOverlay/><ModalContent>
          <ModalHeader fontSize="md">Add New Task</ModalHeader><ModalCloseButton/>
          <ModalBody>
            <VStack spacing={3}>
              <Input size="sm" placeholder="Task title *" value={addTaskForm.task_title}
                onChange={e=>setAddTaskForm(p=>({...p,task_title:e.target.value}))}/>
              <Textarea size="sm" rows={3} placeholder="Description (optional)"
                value={addTaskForm.description} onChange={e=>setAddTaskForm(p=>({...p,description:e.target.value}))}/>
              <Input size="sm" type="date" value={addTaskForm.deadline_date}
                onChange={e=>setAddTaskForm(p=>({...p,deadline_date:e.target.value}))}/>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={addTaskDisc.onClose}>Cancel</Button>
            <Button size="sm" colorScheme="blue" isLoading={saving} isDisabled={!addTaskForm.task_title} onClick={addTask}>Add Task</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
